import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import type {
  Feed,
  ActionConfig,
  ActionType,
  KeywordFilterConfig,
  AIFilterConfig,
  SummarizeConfig,
  TranslateConfig,
  PipelineResult,
} from '@new-signal/shared';
import { apiFetch } from '../lib/api';
import { track } from '../lib/analytics';
import { KeywordFilterCard } from '../components/KeywordFilterCard';
import { AIFilterCard } from '../components/AIFilterCard';
import { SummarizeCard } from '../components/SummarizeCard';
import { TranslateCard } from '../components/TranslateCard';
import { AddStepMenu } from '../components/AddStepMenu';
import { FeedPreview } from '../components/FeedPreview';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { cn } from '../lib/utils';

const labelClassName =
  'block mb-1.5 font-medium text-sm leading-5 text-brand-text';

const baseInputClassName =
  'box-border w-full rounded-[10px] border bg-brand-surface px-3.5 py-3 text-[15px] leading-6 text-brand-text outline-none transition-[border-color,box-shadow,background-color] focus:border-accent-blue focus:bg-white focus:ring-4 focus:ring-accent-blue/10';

const sectionLabelClassName =
  'text-xs font-semibold uppercase tracking-[0.06em] text-brand-muted';

const primaryButtonClassName =
  'inline-flex cursor-pointer items-center justify-center rounded-full border-0 bg-accent-blue px-6 py-3 text-sm font-medium leading-5 text-white hover:bg-accent-blue-hover disabled:cursor-default disabled:bg-[#B0B0B5] disabled:hover:bg-[#B0B0B5]';

function isValidUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function StepIndicator({ current }: { current: 1 | 2 }) {
  const { t } = useTranslation('feed-builder');
  const steps: Array<{ id: 1 | 2; labelKey: string }> = [
    { id: 1, labelKey: 'create.stepIndicator.name' },
    { id: 2, labelKey: 'create.stepIndicator.configure' },
  ];

  return (
    <ol className="mb-8 flex items-center gap-2" aria-label={t('create.stepIndicator.aria')}>
      {steps.map((step, index) => {
        const isActive = step.id === current;
        const isDone = step.id < current;
        return (
          <li key={step.id} className="flex flex-1 items-center gap-2">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold',
                  isActive && 'bg-accent-blue text-white',
                  isDone && 'bg-accent-blue text-white',
                  !isActive && !isDone && 'bg-brand-fill text-brand-muted',
                )}
              >
                {step.id}
              </span>
              <span
                className={cn(
                  'text-sm leading-5',
                  isActive ? 'font-semibold text-brand-text' : 'text-brand-muted',
                )}
              >
                {t(step.labelKey)}
              </span>
            </div>
            {index === 0 && (
              <span
                aria-hidden
                className={cn(
                  'h-px flex-1',
                  current === 2 ? 'bg-accent-blue' : 'bg-brand-border-medium',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function getActionValidationErrors(
  actions: ActionConfig[],
  t: (key: string) => string,
): Record<number, string> {
  const errors: Record<number, string> = {};
  let hasPreviousSummarize = false;

  actions.forEach((action, i) => {
    if (action.type === 'keyword_filter' && action.keywords.length === 0) {
      errors[i] = t('keywordFilter.requiredError');
    }
    if (action.type === 'ai_filter' && action.description.length < 10) {
      errors[i] = t('aiFilter.requiredError');
    }
    if (action.type === 'translate' && action.source === 'summary' && !hasPreviousSummarize) {
      errors[i] = t('translate.summarySourceRequiresSummarize');
    }
    if (action.type === 'summarize') {
      hasPreviousSummarize = true;
    }
  });

  return errors;
}

export function CreateFeedPage() {
  const { t } = useTranslation('feed-builder');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  const [sourceUrls, setSourceUrls] = useState<string[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);

  const [actions, setActions] = useState<ActionConfig[]>([]);
  const [actionValidationErrors, setActionValidationErrors] = useState<Record<number, string>>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [previewConfigChanged, setPreviewConfigChanged] = useState(false);

  const previewEnabled = useFeatureFlag('feed-preview-enabled');

  const handleNameNext = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(t('create.step1.error'));
      return;
    }
    setNameError(null);
    setStep(2);
  };

  const handleAddUrl = (e: FormEvent) => {
    e.preventDefault();
    setUrlError(null);

    const trimmed = urlInput.trim();
    if (!trimmed) return;

    if (!isValidUrl(trimmed)) {
      setUrlError(t('create.step2.urlError'));
      return;
    }

    if (sourceUrls.includes(trimmed)) {
      setUrlError(t('create.step2.duplicateError'));
      return;
    }

    setSourceUrls((prev) => [...prev, trimmed]);
    setUrlInput('');
    setPreviewConfigChanged(true);
  };

  const handleRemoveUrl = (index: number) => {
    setSourceUrls((prev) => prev.filter((_, i) => i !== index));
    setPreviewConfigChanged(true);
  };

  const handleAddAction = (type: ActionType) => {
    if (type === 'keyword_filter') {
      const newAction: KeywordFilterConfig = { type: 'keyword_filter', mode: 'include', keywords: [] };
      setActions((prev) => [...prev, newAction]);
      setPreviewConfigChanged(true);
    }
    if (type === 'ai_filter') {
      const newAction: AIFilterConfig = { type: 'ai_filter', description: '', threshold: 0.7 };
      setActions((prev) => [...prev, newAction]);
      setPreviewConfigChanged(true);
    }
    if (type === 'summarize') {
      const newAction: SummarizeConfig = {
        type: 'summarize',
        length: 'short',
        include_read_time: true,
        output: 'prepend_to_content',
      };
      setActions((prev) => [...prev, newAction]);
      setPreviewConfigChanged(true);
      track('summarize_action_added', {
        length: newAction.length,
        include_read_time: newAction.include_read_time,
      });
    }
    if (type === 'translate') {
      const newAction: TranslateConfig = {
        type: 'translate',
        target_language: 'en',
        source: actions.some((action) => action.type === 'summarize') ? 'summary' : 'content',
        display: 'bilingual',
      };
      setActions((prev) => [...prev, newAction]);
      setPreviewConfigChanged(true);
      track('translate_action_added', {
        target_language: newAction.target_language,
        source: newAction.source,
        display: newAction.display,
      });
    }
  };

  const handleUpdateAction = (index: number, updated: ActionConfig) => {
    setActions((prev) => prev.map((a, i) => (i === index ? updated : a)));
    setActionValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setPreviewConfigChanged(true);
  };

  const handleRemoveAction = (index: number) => {
    const removed = actions[index];
    setActions((prev) => prev.filter((_, i) => i !== index));
    setActionValidationErrors({});
    setPreviewConfigChanged(true);
    if (removed?.type === 'summarize') {
      track('summarize_action_removed', {
        length: removed.length,
        include_read_time: removed.include_read_time,
      });
    }
    if (removed?.type === 'translate') {
      track('translate_action_removed', {
        target_language: removed.target_language,
        source: removed.source,
        display: removed.display,
      });
    }
  };

  const handleRunPreview = async (): Promise<PipelineResult> => {
    const errors = getActionValidationErrors(actions, t);
    if (Object.keys(errors).length > 0) {
      setActionValidationErrors(errors);
      throw new Error(Object.values(errors)[0] ?? t('preview.actionValidationError'));
    }
    setActionValidationErrors({});

    const previewActions = actions.filter((a) => {
      if (a.type === 'keyword_filter') return a.keywords.length > 0;
      if (a.type === 'ai_filter') return a.description.length >= 10;
      return true;
    });
    const res = await apiFetch('/feeds/preview', {
      method: 'POST',
      body: JSON.stringify({ sources: sourceUrls, actions: previewActions }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error((body as { error?: string })?.error ?? 'Preview failed');
    }
    return res.json() as Promise<PipelineResult>;
  };

  const handleSave = async () => {
    const errors = getActionValidationErrors(actions, t);
    if (Object.keys(errors).length > 0) {
      setActionValidationErrors(errors);
      return;
    }
    setActionValidationErrors({});

    setSaving(true);
    setSaveError(null);

    const feedRes = await apiFetch('/feeds', {
      method: 'POST',
      body: JSON.stringify({ name: name.trim(), actions }),
    });

    if (!feedRes.ok) {
      const body = await feedRes.json().catch(() => null);
      setSaveError((body as { error?: string })?.error ?? tc('error.generic'));
      setSaving(false);
      return;
    }

    const feed = (await feedRes.json()) as Feed;

    for (const url of sourceUrls) {
      const srcRes = await apiFetch(`/feeds/${feed.id}/sources`, {
        method: 'POST',
        body: JSON.stringify({ url }),
      });
      if (!srcRes.ok) {
        console.error(`[web] Failed to add source ${url}`);
      }
    }

    track('feed_created', { source_count: sourceUrls.length });
    for (const action of actions) {
      if (action.type === 'summarize') {
        track('summarize_action_saved', {
          length: action.length,
          include_read_time: action.include_read_time,
        });
      }
      if (action.type === 'translate') {
        track('translate_action_saved', {
          target_language: action.target_language,
          source: action.source,
          display: action.display,
        });
      }
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-brand-canvas">
      <header className="mx-auto flex max-w-[640px] items-center justify-between gap-3 px-4 pt-6 sm:px-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-brand-muted no-underline transition-colors hover:text-brand-text"
        >
          <ChevronLeft size={14} strokeWidth={2} />
          <span>{tc('backToDashboard')}</span>
        </Link>
        <LanguageSwitcher />
      </header>

      <main className="mx-auto max-w-[640px] px-4 pb-20 pt-5 sm:px-6">
        <h1 className="mb-6 text-[26px] font-semibold leading-8 tracking-[-0.015em] text-brand-text">
          {t('create.title')}
        </h1>

        <div className="rounded-[18px] border border-brand-border bg-white p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] sm:p-8">
          <StepIndicator current={step} />

          {step === 1 && (
            <form onSubmit={handleNameNext} noValidate className="flex flex-col gap-3.5">
              <div>
                <label htmlFor="feed-name" className={labelClassName}>
                  {t('create.step1.label')}
                </label>
                <input
                  id="feed-name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError(null);
                  }}
                  placeholder={t('create.step1.placeholder')}
                  aria-invalid={Boolean(nameError)}
                  className={cn(
                    baseInputClassName,
                    'text-base',
                    nameError
                      ? 'border-brand-error focus:border-brand-error focus:ring-brand-error/10'
                      : 'border-brand-border-medium',
                  )}
                />
                {nameError ? (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">{nameError}</p>
                ) : (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-muted">
                    {t('create.step1.helper')}
                  </p>
                )}
              </div>

              <button type="submit" className={cn(primaryButtonClassName, 'h-12 w-full text-[15px]')}>
                {t('create.step1.next')}
              </button>
            </form>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-8">
              <section>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h2 className={sectionLabelClassName}>{t('create.sources.title')}</h2>
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate text-sm text-brand-muted">
                      {t('create.feedName')}:{' '}
                      <span className="text-brand-text">{name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm text-brand-text underline-offset-2 hover:underline"
                    >
                      {tc('edit')}
                    </button>
                  </div>
                </div>

                <form onSubmit={handleAddUrl} noValidate className="flex gap-2">
                  <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => {
                      setUrlInput(e.target.value);
                      if (urlError) setUrlError(null);
                    }}
                    placeholder={t('create.step2.placeholder')}
                    aria-invalid={Boolean(urlError)}
                    className={cn(
                      baseInputClassName,
                      'flex-1',
                      urlError
                        ? 'border-brand-error focus:border-brand-error focus:ring-brand-error/10'
                        : 'border-brand-border-medium',
                    )}
                  />
                  <button
                    type="submit"
                    className={cn(primaryButtonClassName, 'shrink-0 rounded-[10px] px-5 py-0')}
                  >
                    {tc('add')}
                  </button>
                </form>

                {urlError && (
                  <p className="mt-1.5 text-[13px] leading-5 text-brand-error">{urlError}</p>
                )}

                {sourceUrls.length === 0 ? (
                  <p className="mt-3 text-sm leading-5 text-brand-muted">
                    {t('create.step2.empty')}
                  </p>
                ) : (
                  <ul className="mt-3 flex flex-col gap-1.5">
                    {sourceUrls.map((url, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between gap-3 rounded-[10px] border border-brand-border bg-brand-surface px-3.5 py-2.5"
                      >
                        <span className="break-all text-sm leading-5 text-brand-text">{url}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveUrl(i)}
                          className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-brand-error hover:underline"
                        >
                          {tc('remove')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h2 className={cn(sectionLabelClassName, 'mb-3')}>{t('create.actions.title')}</h2>

                {actions.length === 0 ? (
                  <p className="mb-3 text-sm leading-5 text-brand-muted">
                    {t('create.actions.empty')}
                  </p>
                ) : (
                  actions.map((action, index) => {
                    if (action.type === 'keyword_filter') {
                      return (
                        <KeywordFilterCard
                          key={index}
                          config={action}
                          onChange={(updated) => handleUpdateAction(index, updated)}
                          onRemove={() => handleRemoveAction(index)}
                          error={actionValidationErrors[index] ?? null}
                        />
                      );
                    }
                    if (action.type === 'ai_filter') {
                      return (
                        <AIFilterCard
                          key={index}
                          config={action}
                          onChange={(updated) => handleUpdateAction(index, updated)}
                          onRemove={() => handleRemoveAction(index)}
                          error={actionValidationErrors[index] ?? null}
                        />
                      );
                    }
                    if (action.type === 'summarize') {
                      return (
                        <SummarizeCard
                          key={index}
                          config={action}
                          onChange={(updated) => handleUpdateAction(index, updated)}
                          onRemove={() => handleRemoveAction(index)}
                        />
                      );
                    }
                    if (action.type === 'translate') {
                      return (
                        <TranslateCard
                          key={index}
                          config={action}
                          onChange={(updated) => handleUpdateAction(index, updated)}
                          onRemove={() => handleRemoveAction(index)}
                          error={actionValidationErrors[index] ?? null}
                        />
                      );
                    }
                    return null;
                  })
                )}

                <AddStepMenu onAdd={handleAddAction} />
              </section>

              {previewEnabled && (
                <section>
                  <h2 className={cn(sectionLabelClassName, 'mb-3')}>{t('create.preview.title')}</h2>
                  <FeedPreview
                    sourceCount={sourceUrls.length}
                    onRunPreview={handleRunPreview}
                    configChanged={previewConfigChanged}
                    onConfigChangeDismiss={() => setPreviewConfigChanged(false)}
                  />
                </section>
              )}

              {saveError && (
                <p
                  className="rounded-lg border border-accent-error-border bg-accent-error-bg px-3 py-2 text-sm text-accent-error-text"
                  role="alert"
                >
                  {saveError}
                </p>
              )}

              <div className="flex items-center justify-between gap-3 border-t border-brand-border pt-6">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-brand-muted hover:text-brand-text"
                >
                  {tc('cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || sourceUrls.length === 0}
                  className={cn(primaryButtonClassName, 'h-11 px-[26px] py-0')}
                >
                  {saving ? tc('saving') : t('create.saveFeed')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
